"use client";

import { RecursiveTranslations } from "@/data/types";
import { h2Style } from "@/data/styles";
import Carousel from "@/components/common/carousel/Carousel";

import img1 from '@/assets/images/gallery-1.webp'
import img2 from '@/assets/images/gallery-2.webp'
import img3 from '@/assets/images/gallery-3.webp'
import img4 from '@/assets/images/gallery-4.webp'
import img5 from '@/assets/images/gallery-5.webp'
import img6 from '@/assets/images/gallery-6.webp'
import img7 from '@/assets/images/gallery-7.webp'
import img8 from '@/assets/images/gallery-8.webp'
import img9 from '@/assets/images/gallery-9.webp'
import img10 from '@/assets/images/gallery-10.webp'
import img11 from '@/assets/images/gallery-11.webp'
import img12 from '@/assets/images/gallery-12.webp'
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import Image from "next/image";

interface Props {
  translations: RecursiveTranslations
}

export default function TopBrandedResidences({ translations }: Props) {
  const data = [
    { image: img1.src }, { image: img2.src }, { image: img3.src }, { image: img4.src },
    { image: img5.src }, { image: img6.src }, { image: img7.src }, { image: img8.src },
    { image: img9.src }, { image: img10.src }, { image: img11.src }, { image: img12.src }
  ];

  const columnRefs = useRef<HTMLDivElement[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  // Trigger parallax only when component is visible
  useEffect(() => {
    if (window.innerWidth < 768) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setInView(true);
          }
        });
      },
      { threshold: 0.01 }
    );

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Scroll-based parallax (relative to component)
  useEffect(() => {
    if (!inView) return;

    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();

      // Start effect earlier: add a "start offset" of 0.3 viewport heights
      const startOffset = window.innerHeight * 0.6;
      const scrollProgress = Math.min(
        Math.max(-(rect.top - startOffset) / rect.height, 0),
        1
      );

      columnRefs.current.forEach((col, idx) => {
        const direction = idx % 2 === 0 ? -1 : 1;
        const maxOffset = 50; // max pixel translation
        if (col) col.style.transform = `translateY(${scrollProgress * maxOffset * direction}px)`;
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [inView]);


  // Split data into 4 columns
  const quarter = Math.ceil(data.length / 4);
  const columns = [
    data.slice(0, quarter),
    data.slice(quarter, 2 * quarter),
    data.slice(2 * quarter, 3 * quarter),
    data.slice(3 * quarter)
  ];

  return (
    <div
      ref={containerRef}
      className="max-w-[1440px] mx-auto px-[20px] md:px-[50px] py-[60px] flex flex-col gap-[30px] text-[#1B1D2B] text-center"
    >
      <h2 className={clsx(h2Style, "text-center")}>{translations?.title as string}</h2>

      {/* Mobile & Tablet: Carousel */}
      <div className="block lg:hidden">
        <Carousel data={data} />
      </div>

      {/* Desktop: Parallax Grid */}
      <div className="hidden lg:grid grid-cols-1 lg:grid-cols-4 gap-x-8 gap-y-4 my-10">
        {columns.map((column, idx) => (
          <div
            key={idx}
            ref={el => {columnRefs.current[idx] = el!}}
            className="flex flex-col gap-10 transition-transform duration-300"
          >
            {column.map((item, i) => (
              <div
                key={i}
                className="aspect-[3/4] w-full overflow-hidden rounded-lg"
              >
                <Image src={item.image} width={400} height={500} className="h-full w-full object-cover" alt="gallery" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
